import React, { useState, useEffect } from "react";
import { Category, ProductFilters } from "../../types";
import CategoryBanner from "../../assets/banner/bannerCategory.jpeg";
import Loader from "../loader/Loader";
import { getCategoryChain } from "../../utils/categoryUtils";

interface FiltersPanelProps {
    categories: Category[];
    categoriesLoading: boolean;
    categoriesError: string | null;
    filters: ProductFilters;
    applyFilters: (newFilters: ProductFilters) => void;
    resetFilters: () => void;
}

export default function FiltersPanel({
    categories,
    categoriesLoading,
    categoriesError,
    filters,
    applyFilters,
    resetFilters,
}: FiltersPanelProps) {
    const [localFilters, setLocalFilters] = useState<ProductFilters>(filters);
    const [selectedPath, setSelectedPath] = useState<Category[]>([]);
    const [expandedLevels, setExpandedLevels] = useState<boolean[]>([true]);

    // Инициализируем selectedPath на основе category_id из фильтров
    useEffect(() => {
        if (filters.category_id && categories.length > 0) {
            const categoryChain = getCategoryChain(categories, filters.category_id);
            if (categoryChain.length > 0) {
                setSelectedPath(categoryChain);
                // Устанавливаем expandedLevels: все уровни кроме последнего должны быть свернуты
                const newExpandedLevels = new Array(categoryChain.length).fill(false);
                newExpandedLevels[newExpandedLevels.length - 1] = true; // последний уровень раскрыт
                setExpandedLevels(newExpandedLevels);
            }
        } else {
            // Если нет category_id, сбрасываем selectedPath
            setSelectedPath([]);
            setExpandedLevels([true]);
        }
    }, [filters.category_id, categories]);

    // Синхронизируем localFilters с filters
    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const getCategoriesAtLevel = (level: number): Category[] => {
        if (level === 0) {
            return categories;
        } else {
            const parent = selectedPath[level - 1];
            return parent && parent.children ? parent.children : [];
        }
    };

    const handleClick = (category: Category, level: number) => {
        let newPath = [...selectedPath];
        let newExpanded = [...expandedLevels];

        if (newPath[level] && newPath[level].id === category.id) {
            if (newPath.length > level + 1) {
                newPath = newPath.slice(0, level + 1);
                newExpanded[level + 1] = true;
            } else {
                newPath = newPath.slice(0, level);
                newExpanded[level] = true;
            }
        } else {
            newPath = newPath.slice(0, level);
            newPath[level] = category;
            newExpanded[level] = false;
            if (category.children && category.children.length > 0) {
                newExpanded[level + 1] = true;
            } else {
                newExpanded = newExpanded.slice(0, level + 1);
            }
        }

        setSelectedPath(newPath);
        setExpandedLevels(newExpanded);
        setLocalFilters({ ...localFilters, category_id: category.id });
    };

    const handleApplyFilters = () => {
        applyFilters({ ...localFilters, offset: 0 });
    };

    const renderList = (level: number, cats: Category[]) => {
        let listToRender = cats;
        
        // Если есть выбранная категория на этом уровне и уровень свернут
        if (selectedPath[level] && expandedLevels[level] === false) {
            listToRender = [selectedPath[level]!];
        }
        
        return (
            <ul key={level} style={{
                listStyle: "none",
                margin: 0,
                padding: "8px 0",
                marginLeft: level * 20,
                fontSize: "14px",
            }}>
                {listToRender.map((cat) => {
                    const isSelected = selectedPath[level]?.id === cat.id;
                    return (
                        <li
                            key={cat.id}
                            onClick={() => handleClick(cat, level)}
                            style={{
                                padding: "8px 0",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid #e0e0e0",
                                cursor: "pointer",
                            }}
                        >
                            <span>{cat.name}</span>
                            <input type="checkbox" readOnly checked={isSelected} />
                        </li>
                    );
                })}
            </ul>
        );
    };

    const levelsToRender: React.ReactNode[] = [];
    let level = 0;
    
    // Если есть выбранная категория, показываем только её и её подкатегории
    if (selectedPath.length > 0) {
        // Показываем выбранную категорию
        levelsToRender.push(renderList(0, [selectedPath[0]!]));
        
        // Показываем подкатегории выбранной категории
        if (selectedPath[0]?.children && selectedPath[0].children.length > 0) {
            levelsToRender.push(renderList(1, selectedPath[0].children));
        }
    } else {
        // Если нет выбранной категории, показываем все корневые категории
        const cats = getCategoriesAtLevel(0);
        if (cats && cats.length > 0) {
            levelsToRender.push(renderList(0, cats));
        }
    }

    return (
        <div className="w-full lg:w-1/4">
            <div className="mb-8 border rounded-lg p-4">
                <img src={CategoryBanner} alt="Баннер" className="mx-auto" />
            </div>

            <div className="space-y-4 border rounded-lg p-4">
                <div className="flex justify-between items-center">
                    <h3 className="font-bold text-lg">Фильтры</h3>
                    <button
                        onClick={() => {
                            resetFilters();
                            setSelectedPath([]);
                            setExpandedLevels([true]);
                            setLocalFilters({
                                limit: 10,
                                offset: 0
                            });
                        }}
                        className="text-sm text-red-500 underline"
                    >
                        Сбросить
                    </button>
                </div>

                <div>
                    <h4 className="font-semibold mb-2">Цена</h4>
                    <div className="flex items-center space-x-2">
                        <input
                            type="number"
                            placeholder="0"
                            className="border rounded p-2 w-1/2"
                            value={localFilters.min_price || ""}
                            onChange={(e) =>
                                setLocalFilters({ ...localFilters, min_price: Number(e.target.value) || undefined })
                            }
                        />
                        <span className="text-gray-500">-</span>
                        <input
                            type="number"
                            placeholder="100"
                            className="border rounded p-2 w-1/2"
                            value={localFilters.max_price || ""}
                            onChange={(e) =>
                                setLocalFilters({ ...localFilters, max_price: Number(e.target.value) || undefined })
                            }
                        />
                    </div>
                </div>

                {/* <div>
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={localFilters.inStock || false}
                            onChange={(e) => setLocalFilters({ ...localFilters, inStock: e.target.checked })}
                            className="custom-checkbox"
                        />
                        <span>В наличии</span>
                    </label>
                </div> */}

                {categoriesLoading ? (
                    <Loader />
                ) : categoriesError ? (
                    <p>Ошибка загрузки категорий</p>
                ) : (
                    <div>
                        <h4 className="font-semibold">Категории</h4>
                        <div>{levelsToRender}</div>
                    </div>
                )}

                <button
                    onClick={handleApplyFilters}
                    className="mt-4 bg-[#2C2C2C] text-white px-4 py-2 rounded w-full"
                >
                    Применить
                </button>
            </div>
        </div>
    );
}
